import React from "react";
import { Box, Stack, Typography, Chip, Tooltip, Button, IconButton } from "@mui/material";
import PsychologyIcon from "@mui/icons-material/Psychology";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import EventNoteIcon from "@mui/icons-material/EventNote";
import NotesIcon from "@mui/icons-material/Notes";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useNavigate } from "react-router-dom";
import { getLaceColor, ALERT_COLORS, SP_VISIT_COLORS, getSpVisitAge } from "../../constants/clinicalDashboard";

dayjs.extend(customParseFormat);

/**
 * Card individual de paciente en alerta (admitted/discharged/emergency).
 * Muestra nombre, facility, fecha, LACE score, diagnoses, PCP.
 */
export default function AlertPatientCard({ patient, type, onClick, onCreateTask }) {
  const navigate = useNavigate();
  const borderColor = ALERT_COLORS[type] || "#94A3B8";
  const laceInfo = getLaceColor(patient.lace);

  // Calcular días desde admisión
  const admitDate = patient.admitDate ? dayjs(patient.admitDate, "MM/DD/YYYY") : null;
  const daysSince = admitDate?.isValid() ? dayjs().diff(admitDate, "day") : null;

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        borderLeft: `4px solid ${borderColor}`,
        bgcolor: "background.paper",
        cursor: onClick ? "pointer" : "default",
        transition: "background-color 150ms ease",
        "&:hover": onClick ? { bgcolor: "#F8FAFB" } : {},
      }}
    >
      <Stack spacing={0.75}>
        {/* Nombre */}
        <Typography variant="body2" fontWeight={700} sx={{ fontSize: "0.8rem" }}>
          {patient.name}
        </Typography>

        {/* Facility */}
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
          {patient.facility}
        </Typography>

        {/* Fecha + días */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap" }}>
          {patient.admitDate && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
              Admit: {patient.admitDate}
            </Typography>
          )}
          {patient.dischargeDate && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
              DC: {patient.dischargeDate}
            </Typography>
          )}
          {daysSince != null && (
            <Chip
              label={`${daysSince}d`}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.62rem",
                fontWeight: 600,
                bgcolor: daysSince > 7 ? "#FEE2E2" : "#FEF3C7",
                color: daysSince > 7 ? "#991B1B" : "#92400E",
              }}
            />
          )}
        </Stack>

        {/* LACE + BH flag + diagnoses */}
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: "wrap" }}>
          {patient.lace != null && (
            <Chip
              label={`LACE: ${patient.lace}`}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 600,
                borderRadius: "10px",
                bgcolor: `${laceInfo.color}18`,
                color: laceInfo.color,
                border: `1px solid ${laceInfo.color}40`,
              }}
            />
          )}
          {patient.behavioralHealth && (
            <Tooltip title="Behavioral Health">
              <PsychologyIcon sx={{ fontSize: 16, color: "#7C3AED" }} />
            </Tooltip>
          )}
          {patient.disposition && (
            <Chip
              label={patient.disposition}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.6rem",
                bgcolor: patient.disposition === "Expired" ? "#FEE2E2" : "#F1F5F9",
                color: patient.disposition === "Expired" ? "#991B1B" : "#475569",
              }}
            />
          )}
        </Stack>

        {/* Diagnoses */}
        {patient.diagnoses && (
          <Tooltip title={patient.diagnoses}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "0.65rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 220,
              }}
            >
              Dx: {patient.diagnoses}
            </Typography>
          </Tooltip>
        )}

        {/* Productivity indicators */}
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: "wrap" }}>
          <SpVisitIndicator date={patient.lastSantePlusVisit} />
          {(patient.observationsCount || 0) > 0 && (
            <Chip
              icon={<NotesIcon sx={{ fontSize: 12 }} />}
              label={`${patient.observationsCount} obs`}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.58rem",
                fontWeight: 600,
                bgcolor: "#DBEAFE",
                color: "#1E40AF",
                "& .MuiChip-icon": { color: "#1E40AF", ml: 0.25 },
              }}
            />
          )}
          {(patient.activeTasks || 0) > 0 && (
            <Chip
              icon={<AssignmentIcon sx={{ fontSize: 12 }} />}
              label={`${patient.activeTasks} tasks`}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.58rem",
                fontWeight: 600,
                bgcolor: "#FEF3C7",
                color: "#92400E",
                "& .MuiChip-icon": { color: "#92400E", ml: 0.25 },
              }}
            />
          )}
        </Stack>

        {/* PCP + Follow Up */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
            {patient.pcpName}
          </Typography>
          <Tooltip title="View Patient">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); navigate(`/patient-info/${patient.cipId}`); }}
              sx={{ color: "#6366F1", p: 0.25 }}
            >
              <VisibilityIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          {onCreateTask && (
            <Button
              size="small"
              startIcon={<PlaylistAddIcon sx={{ fontSize: 12 }} />}
              onClick={(e) => {
                e.stopPropagation();
                onCreateTask(patient, type);
              }}
              sx={{
                fontSize: "0.62rem",
                textTransform: "none",
                minWidth: 0,
                px: 0.75,
                py: 0,
                height: 22,
                borderRadius: 1.5,
                color: "#2563EB",
              }}
            >
              Follow Up
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

// ─── Sub-components ─────────────────────────────────────────────

function SpVisitIndicator({ date }) {
  const age = getSpVisitAge(date);
  const colors = SP_VISIT_COLORS[age];

  if (!date) {
    return (
      <Chip
        icon={<EventNoteIcon sx={{ fontSize: 12 }} />}
        label="No SP visit"
        size="small"
        sx={{
          height: 18,
          fontSize: "0.58rem",
          fontWeight: 600,
          bgcolor: colors.bg,
          color: colors.text,
          "& .MuiChip-icon": { color: colors.text, ml: 0.25 },
        }}
      />
    );
  }

  const d = new Date(date);
  const label = `SP: ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;

  return (
    <Chip
      icon={<EventNoteIcon sx={{ fontSize: 12 }} />}
      label={label}
      size="small"
      sx={{
        height: 18,
        fontSize: "0.58rem",
        fontWeight: 600,
        bgcolor: colors.bg,
        color: colors.text,
        "& .MuiChip-icon": { color: colors.text, ml: 0.25 },
      }}
    />
  );
}
